import { SPORTS, getSportCategoryId, getSportLabel, normalizeSportId, type SportId } from './sports';

export type MatchCopyStyle = 'game' | 'workout' | 'session' | 'battle' | 'run';

export type MatchCopyContext = {
  copyStyle: MatchCopyStyle;
  sportId: string | null;
  variant: string | null;
  activityLabel: string | null;
};

export type MatchCopyScope =
  | { kind: 'base' }
  | { kind: 'style'; copyStyle: MatchCopyStyle }
  | { kind: 'sport'; sportId: string }
  | { kind: 'variant'; sportId: string; variant: string };

export const MATCH_COPY_STYLE_OPTIONS: Array<{ value: MatchCopyStyle; label: string }> = [
  { value: 'game', label: 'Game' },
  { value: 'workout', label: 'Workout' },
  { value: 'session', label: 'Session' },
  { value: 'battle', label: 'Battle' },
  { value: 'run', label: 'Run' },
];

export const MATCH_COPY_SPORT_OPTIONS = [...SPORTS]
  .sort((a, b) => getSportLabel(a).localeCompare(getSportLabel(b)))
  .map((sportId) => ({ value: sportId, label: getSportLabel(sportId) }));

function trickOptions(sportId: string) {
  const word = sportId === 'skateboarding' ? 'SKATE' : sportId === 'scooter' ? 'SCOOT' : 'TRICKS';
  return [
    { value: 'free_session', label: 'Open session' },
    { value: 'game_of_skate', label: `Game of ${word}` },
  ];
}

export const MATCH_COPY_VARIANT_OPTIONS: Record<string, Array<{ value: string; label: string }>> = {
  pinball: [{ value: 'high_score', label: 'High score' }],
  weightlifting: [
    { value: 'one_rep_max', label: 'One Rep Max' },
    { value: 'max_reps', label: 'Max reps' },
  ],
  crossfit: [
    { value: 'for_time', label: 'For time' },
    { value: 'amrap', label: 'AMRAP' },
    { value: 'max_reps', label: 'Max reps' },
  ],
  hyrox: [
    { value: 'for_time', label: 'For time' },
    { value: 'amrap', label: 'AMRAP' },
  ],
  skateboarding: trickOptions('skateboarding'),
  'bmx flatground': trickOptions('bmx flatground'),
  scooter: trickOptions('scooter'),
  'inline tricks': trickOptions('inline tricks'),
};

export const MATCH_COPY_CONTEXT_DEFAULTS = {
  controls: {
    hostSection: {
      scoreChallengeReadySubtitle: 'Start when ready.',
    },
  },
  contexts: {
    styles: {},
    sports: {
      weightlifting: {
        actions: {
          finish_match: { label: 'Review workout' },
        },
      },
    },
    variants: {},
  },
} as const;

function normalizeFreeform(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed.toLowerCase() : null;
}

export function normalizeMatchCopyVariant(value: unknown): string | null {
  return normalizeFreeform(value)?.replace(/[\s-]+/g, '_') ?? null;
}

export function normalizeMatchCopySportId(value: unknown): string | null {
  return normalizeSportId(value) ?? normalizeFreeform(value);
}

export function getMatchCopyStyleForSport(sportId: SportId | string | null | undefined): MatchCopyStyle {
  const normalizedSportId = normalizeSportId(sportId);
  switch (normalizedSportId ? getSportCategoryId(normalizedSportId) : null) {
    case 'gym-performance':
      return 'workout';
    case 'trick-style':
      return normalizedSportId === 'parkour' ? 'run' : normalizedSportId === 'breakdancing' ? 'battle' : 'session';
    case 'combat':
      return 'battle';
    case 'foot-race':
      return 'run';
    default:
      return 'game';
  }
}

function getMatchCopyStyleForVariant(args: {
  sportId?: SportId | string | null;
  variant?: string | null;
  fallbackStyle: MatchCopyStyle;
}): MatchCopyStyle {
  const sportId = normalizeSportId(args.sportId);
  const variant = normalizeMatchCopyVariant(args.variant);
  if (!sportId || !variant) return args.fallbackStyle;
  if (getSportCategoryId(sportId) !== 'trick-style') return args.fallbackStyle;
  return variant === 'game_of_skate' ? 'game' : args.fallbackStyle;
}

export function buildMatchCopyContext(args: {
  sportId?: SportId | string | null;
  variant?: string | null;
  activityLabel?: string | null;
  copyStyle?: MatchCopyStyle | null;
}): MatchCopyContext {
  const sportId = normalizeMatchCopySportId(args.sportId);
  const variant = normalizeMatchCopyVariant(args.variant);
  const fallbackStyle = getMatchCopyStyleForSport(sportId);
  return {
    copyStyle: args.copyStyle ?? getMatchCopyStyleForVariant({ sportId, variant, fallbackStyle }),
    sportId,
    variant,
    activityLabel:
      typeof args.activityLabel === 'string' && args.activityLabel.trim().length > 0
        ? args.activityLabel.trim()
        : null,
  };
}

export function buildMatchCopyLookupPaths(path: string, context?: Partial<MatchCopyContext> | null): string[] {
  const normalizedPath = path.trim();
  if (!normalizedPath) return [];
  return buildMatchCopyLookupRoots(context)
    .map((root) => `${root}.${normalizedPath}`)
    .concat(normalizedPath);
}

export function buildMatchCopyLookupRoots(context?: Partial<MatchCopyContext> | null): string[] {
  const output: string[] = [];
  const sportId = normalizeMatchCopySportId(context?.sportId);
  const variant = normalizeMatchCopyVariant(context?.variant);
  const copyStyle = context?.copyStyle ?? getMatchCopyStyleForSport(sportId);
  if (sportId && variant) output.push(`contexts.variants.${sportId}.${variant}`);
  if (sportId) output.push(`contexts.sports.${sportId}`);
  if (copyStyle) output.push(`contexts.styles.${copyStyle}`);
  return output;
}

export function getMatchCopyScopedPath(path: string, scope: MatchCopyScope): string {
  switch (scope.kind) {
    case 'style':
      return `contexts.styles.${scope.copyStyle}.${path}`;
    case 'sport':
      return `contexts.sports.${scope.sportId}.${path}`;
    case 'variant':
      return `contexts.variants.${scope.sportId}.${scope.variant}.${path}`;
    case 'base':
    default:
      return path;
  }
}
